import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, input, label, sectionLabel, btn, RGB, Icon, ICONS, errMsg } from './extUi';

// Add / edit a tournament team: name, tag, 5 active players + up to 3 substitutes (IGN + Steam ID).
// Saving goes on to the team's stage seating page. Routes: /admin/tournaments/:id/teams/new,
// /admin/tournaments/:id/teams/:teamId/edit.

const ACTIVE = 5, SUBS = 3;
const blankRow = () => ({ ign: '', steam_id: '' });

export default function ExtTeamEdit() {
  const { t } = useTranslation();
  const { id, teamId } = useParams();
  const navigate = useNavigate();
  const isNew = !teamId;
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [active, setActive] = useState(() => Array.from({ length: ACTIVE }, blankRow));
  const [subs, setSubs] = useState(() => Array.from({ length: SUBS }, blankRow));
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    axios.get(`/api/v1/ext/tournaments/${id}/teams/${teamId}`).then(r => {
      const tm = r.data;
      setName(tm.name); setTag(tm.tag || '');
      const a = tm.players.filter(p => p.role === 'active').sort((x, y) => x.seat - y.seat).map(p => ({ ign: p.ign, steam_id: p.steam_id }));
      const s = tm.players.filter(p => p.role === 'sub').map(p => ({ ign: p.ign, steam_id: p.steam_id }));
      setActive([...a, ...Array.from({ length: Math.max(0, ACTIVE - a.length) }, blankRow)].slice(0, ACTIVE));
      setSubs([...s, ...Array.from({ length: Math.max(0, SUBS - s.length) }, blankRow)].slice(0, SUBS));
      setLoading(false);
    }).catch(e => { setError({ message: errMsg(e, t('ext.loadFailed', 'Failed to load')) }); setLoading(false); });
  }, [isNew, id, teamId, t]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    // Seats follow the order of the active rows on a new team (and keep their seat on an edit).
    const players = [
      ...active.map((p, i) => ({ ...p, role: 'active', seat: isNew ? i + 1 : undefined })),
      ...subs.map(p => ({ ...p, role: 'sub' })),
    ];
    try {
      const r = isNew
        ? await axios.post(`/api/v1/ext/tournaments/${id}/teams`, { name, tag, players })
        : await axios.put(`/api/v1/ext/tournaments/${id}/teams/${teamId}`, { name, tag, players });
      navigate(`/admin/tournaments/${id}/teams/${r.data.id}/seating`);
    } catch (err) {
      const d = err.response && err.response.data && err.response.data.error;
      setError({ message: errMsg(err, t('ext.saveFailed', 'Could not save')), index: d && Number.isInteger(d.index) ? d.index : null });
    } finally { setBusy(false); }
  };

  if (loading) return <div style={sub}>{t('common.loading', 'Loading...')}</div>;

  // error.index counts every row the server got, including empty ones: active rows first, then subs.
  const rowList = (rows, setRows, offset, title, hint) => (
    <div style={{ marginTop: '1rem' }}>
      <div style={sectionLabel}>{title}</div>
      <p style={{ ...sub, margin: '0.2rem 0 0.5rem' }}>{hint}</p>
      <div style={{ display: 'grid', gap: '0.45rem' }}>
        {rows.map((p, i) => {
          const bad = error && error.index === offset + i;
          const set = (k) => (e) => setRows(rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
          return (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', color: 'var(--text-muted)', width: 22 }}>{offset + i + 1}</span>
              <input style={{ ...input, flex: '1 1 180px', width: 'auto', borderColor: bad ? 'var(--accent-red)' : undefined }} value={p.ign} onChange={set('ign')} maxLength={32}
                placeholder={t('extTeams.ignPlaceholder', 'IGN')} aria-label={t('extTeams.ign', 'IGN')} />
              <input style={{ ...input, flex: '2 1 240px', width: 'auto', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem', borderColor: bad ? 'var(--accent-red)' : undefined }} value={p.steam_id} onChange={set('steam_id')}
                placeholder={t('extTeams.steamPlaceholder', 'Steam ID (7656…, STEAM_0:… or [U:1:…])')} aria-label={t('extTeams.steamId', 'Steam ID')} />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to={`/admin/tournaments/${id}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('extTeams.backToTournament', 'Back to the tournament')}</Link>
        <h1 style={{ ...h1, marginTop: '0.35rem' }}>{isNew ? t('extTeams.newTeam', 'New team') : t('extTeams.editTeam', 'Edit team')}</h1>
      </div>
      <form onSubmit={save} style={card}>
        <h2 style={h2}>{t('extTeams.teamDetails', 'Team')}</h2>
        <p style={sub}>{t('extTeams.teamDetailsHint', 'The IGNs entered here replace the players’ Steam names everywhere in this tournament’s data (matched by Steam ID). Next you set the team’s stage seating.')}</p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '3 1 260px' }}><label style={label}>{t('ext.teamName', 'Name')}</label><input required style={input} value={name} onChange={e => setName(e.target.value)} maxLength={120} /></div>
          <div style={{ flex: '1 1 120px' }}><label style={label}>{t('ext.teamTag', 'Tag')}</label><input style={input} value={tag} onChange={e => setTag(e.target.value)} maxLength={16} /></div>
        </div>
        {rowList(active, setActive, 0, t('extTeams.activePlayers', 'Active players (5)'), t('extTeams.activeHint', 'All 5 are required. On a new team they take seats 1–5 in this order; you can change the seating on the next page.'))}
        {rowList(subs, setSubs, ACTIVE, t('extTeams.substitutes', 'Substitutes (up to 3)'), t('extTeams.subsHint', 'Optional. Leave a row empty if the team has fewer substitutes.'))}
        {error && <div style={{ marginTop: '0.9rem', fontSize: '0.82rem', color: 'var(--accent-red)' }}>{error.message}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.1rem' }}>
          <Link to={`/admin/tournaments/${id}`} style={{ ...btn(RGB.gray), textDecoration: 'none' }}>{t('common.cancel', 'Cancel')}</Link>
          <button type="submit" disabled={busy} style={btn(RGB.green, true)}>{busy ? t('common.loading', 'Loading...') : t('extTeams.saveAndSeat', 'Save & set seating')} →</button>
        </div>
      </form>
    </div>
  );
}
